/* eslint-disable */
import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import API from "../lib/api";
import { Section } from "../components/Common";
import { PractitionerCard } from "../components/PractitionerRecommendations";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Search, ArrowRight, Plus,
  Building2, Brain, Apple, Dumbbell, Trophy, Activity, Mountain, Flame, Users, HeartHandshake,
} from "lucide-react";

const ICONS = { Brain, Apple, Dumbbell, Trophy, Activity, Mountain, Building2, Flame, Users, HeartHandshake };

// Topic slug -> i18n key suffix for the "Filtering by topic" chip
const TOPIC_SLUGS = [
  "mental-health","mens-wellness","womens-wellness","menopause","family-support","teens",
  "couples","relationships","nutrition","fitness","sleep","longevity","burnout","corporate",
  "ayurveda","yoga","retreat",
];

export default function MarketplacePage() {
  const { t } = useTranslation();
  const TOPIC_LABELS = TOPIC_SLUGS.reduce((acc, k) => {
    acc[k] = t(`marketplace.topic_labels.${k}`, k);
    return acc;
  }, {});
  const { category } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth() || {};

  const topic = searchParams.get("topic") || "";
  const dosha = searchParams.get("dosha") || "";

  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [featured, setFeatured] = useState({ items: [], luvconnect: null });
  const [favIds, setFavIds] = useState(new Set());
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/marketplace/categories").then(({ data }) => setCategories(data.categories || []));
    API.get("/marketplace/featured").then(({ data }) => setFeatured(data || {}));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (q) params.set("q", q);
    if (country) params.set("country", country);
    if (topic) params.set("topic", topic);
    if (dosha) params.set("dosha", dosha);
    API.get(`/marketplace/listings?${params.toString()}`)
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, [category, q, country, topic, dosha]);

  useEffect(() => {
    if (!user) { setFavIds(new Set()); return; }
    API.get("/marketplace/favorites/ids")
      .then(({ data }) => setFavIds(new Set(data.ids || [])))
      .catch(() => setFavIds(new Set()));
  }, [user]);

  const toggleFav = async (b) => {
    if (!user) {
      toast.info(t("marketplace.login_to_save", "Sign in to save practitioners"));
      return;
    }
    const wasFav = favIds.has(b.business_id);
    setFavIds((prev) => {
      const next = new Set(prev);
      if (wasFav) next.delete(b.business_id); else next.add(b.business_id);
      return next;
    });
    try {
      if (wasFav) {
        await API.delete(`/marketplace/favorites/${b.business_id}`);
        toast.success(t("marketplace.removed_from_saved", "Removed from saved"));
      } else {
        await API.post(`/marketplace/favorites/${b.business_id}`);
        toast.success(t("marketplace.added_to_saved", "Saved to your practitioners"));
      }
    } catch {
      setFavIds((prev) => {
        const next = new Set(prev);
        if (wasFav) next.add(b.business_id); else next.delete(b.business_id);
        return next;
      });
      toast.error(t("marketplace.fav_error", "Could not update saved practitioners"));
    }
  };

  const clearTopicFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("topic");
    next.delete("dosha");
    setSearchParams(next, { replace: true });
  };

  return (
    <div data-testid="page-marketplace">
      <Section eyebrow={t("marketplace.eyebrow")} title={t("marketplace.title")} subtitle={t("marketplace.subtitle")}>
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("marketplace.search_placeholder")} className="w-full bg-[#0F1116] border border-white/10 rounded-lg ps-9 pe-3 py-3 text-sm focus:outline-none focus:border-[#C8A24A]" data-testid="mkt-search" />
          </div>
          <input value={country} onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0, 2))} placeholder={t("marketplace.country_placeholder", "Country (US, FR…)")} className="w-32 bg-[#0F1116] border border-white/10 rounded-lg px-3 py-3 text-sm" data-testid="mkt-country" />
          <button onClick={() => navigate("/marketplace")} className={`chip ${!category ? "chip-active" : ""}`} data-testid="mkt-all">{t("marketplace.all")}</button>
          <Link to="/marketplace/register" className="btn-secondary text-sm inline-flex items-center gap-2" data-testid="mkt-list-cta"><Plus className="w-4 h-4" /> {t("marketplace.list_business_cta")}</Link>
        </div>

        {(topic || dosha) && (
          <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="mkt-active-filter">
            <span className="text-xs uppercase tracking-[0.18em] text-white/55">
              {t("marketplace.filtering_by", "Filtering by")}:
            </span>
            {topic && (
              <span className="chip chip-active inline-flex items-center gap-1">
                {TOPIC_LABELS[topic] || topic.replace(/-/g, " ")}
              </span>
            )}
            {dosha && (
              <span className="chip chip-active inline-flex items-center gap-1 capitalize">
                {dosha}
              </span>
            )}
            <button onClick={clearTopicFilter} className="text-xs text-white/65 underline" data-testid="mkt-clear-filter">
              {t("marketplace.clear_filter", "Clear")}
            </button>
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-2" data-testid="mkt-categories">
          {categories.map((c) => {
            const Icon = ICONS[c.icon] || Building2;
            return (
              <button key={c.slug} onClick={() => navigate(`/marketplace/category/${c.slug}`)} className={`chip ${category === c.slug ? "chip-active" : ""} inline-flex items-center gap-2`} data-testid={`mkt-chip-${c.slug}`}>
                <Icon className="w-3.5 h-3.5" />{c.title}
              </button>
            );
          })}
        </div>
      </Section>

      {!category && !topic && !dosha && featured.luvconnect && (
        <Section eyebrow={t("relationships.featured_eyebrow")} title={featured.luvconnect.name} subtitle={featured.luvconnect.tagline}>
          <div className="card-surface p-6 md:p-10 grid md:grid-cols-3 gap-6 items-center" data-testid="luvconnect-card">
            <div className="md:col-span-2">
              <p className="text-white/85 leading-relaxed body-copy">{featured.luvconnect.description}</p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-2 text-sm text-white/85">
                {(featured.luvconnect.highlights || []).map((h, i) => (
                  <li key={i} className="flex gap-2"><span className="gold-text">◆</span>{h}</li>
                ))}
              </ul>
              <a href={featured.luvconnect.cta_url} target="_blank" rel="noreferrer" className="btn-primary inline-flex items-center gap-2 mt-6" data-testid="luv-cta">
                {t("relationships.luv_cta")} <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
              <img src={featured.luvconnect.image} alt="Luvconnect" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-transparent to-transparent" />
            </div>
          </div>
        </Section>
      )}

      <div className="app-container pb-20">
        {loading ? (
          <div className="text-white/60 text-sm">{t("common.loading")}</div>
        ) : items.length === 0 ? (
          <div className="text-white/60 text-sm" data-testid="mkt-empty">{t("marketplace.empty")}</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mkt-grid">
            {items.map((b) => (
              <PractitionerCard
                key={b.business_id}
                b={b}
                isFavorite={favIds.has(b.business_id)}
                onToggleFavorite={() => toggleFav(b)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
